import React from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Plus } from "lucide-react";
import { useApp } from "@/contexts/AppContext";
import LucideIcon from "@/components/LucideIcon";
import { SCENARIO } from "@/constants/testIds";
import { toast } from "sonner";

export default function ScenarioDetailPage() {
  const { slug } = useParams();
  const { scenarios, products, t, localized, selectedScenarios, toggleScenario, loading } = useApp();

  const scenario = scenarios.find((s) => s.slug === slug);
  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-5 sm:px-10 py-24 text-gray-400">Loading…</div>
    );
  }
  if (!scenario) {
    return (
      <div className="max-w-7xl mx-auto px-5 sm:px-10 py-24">
        <Link to="/scenarios" className="text-amber-400 hover:underline">← Back</Link>
        <h1 className="mt-6 font-heading text-3xl">Scenario not found</h1>
      </div>
    );
  }

  const isSelected = selectedScenarios.includes(slug);
  const requiredProducts = scenario.products
    .map((p) => products.find((x) => x.slug === p))
    .filter(Boolean);

  return (
    <section data-testid={SCENARIO.detailRoot} className="max-w-7xl mx-auto px-5 sm:px-10 py-12 sm:py-20">
      <Link
        to="/scenarios"
        data-testid={SCENARIO.detailBack}
        className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white"
      >
        <ArrowLeft className="h-4 w-4" />
        {t.scenario.back}
      </Link>

      <div className="mt-8 grid md:grid-cols-12 gap-10">
        {/* Left: visuals */}
        <div className="md:col-span-7">
          <div
            className="relative aspect-[5/4] sm:aspect-[16/10] rounded-3xl overflow-hidden border border-white/[0.06]"
            style={{
              background: `radial-gradient(120% 80% at 0% 0%, ${scenario.color}25, transparent 55%), #0B0F19`,
            }}
          >
            <div
              className="absolute inset-0 opacity-75"
              style={{
                backgroundImage: `url(${scenario.image})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                filter: "saturate(0.9)",
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19] via-[#0B0F19]/30 to-transparent" />
            <div className="relative h-full p-8 sm:p-10 flex flex-col justify-between">
              <div
                className="inline-flex h-12 w-12 items-center justify-center rounded-xl border"
                style={{
                  borderColor: `${scenario.color}55`,
                  background: `${scenario.color}1A`,
                  color: scenario.color,
                }}
              >
                <LucideIcon name={scenario.icon} className="h-6 w-6" strokeWidth={2} />
              </div>

              <div>
                <span
                  className="inline-block text-[11px] tracking-[0.25em] uppercase font-bold mb-3"
                  style={{ color: scenario.color }}
                >
                  {localized(scenario, "tagline")}
                </span>
                <h1 className="font-heading font-medium text-4xl sm:text-5xl tracking-tight leading-[1.05]">
                  {localized(scenario, "title")}
                </h1>
              </div>
            </div>
          </div>

          {/* Trigger -> Action interactive */}
          <div className="mt-10 rounded-3xl border border-white/[0.06] bg-[#0B0F19] p-7 sm:p-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
              <FlowBlock
                label={t.scenario.trigger}
                step={scenario.trigger}
                color={scenario.color}
                localized={localized}
              />
              <FlowBlock
                label={t.scenario.action}
                step={scenario.action}
                color={scenario.color}
                localized={localized}
                solid
              />
            </div>

            <div className="mt-8 flow-line h-px bg-white/5" />

            <div className="mt-8">
              <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-gray-400 mb-4">
                {t.scenario.flow}
              </div>
              <ol className="space-y-3">
                {localized(scenario, "flow").map((step, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -8 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.08 }}
                    className="flex gap-3 items-start"
                  >
                    <span
                      className="mt-0.5 font-mono-tight text-xs px-2 py-0.5 rounded-md border"
                      style={{
                        borderColor: `${scenario.color}55`,
                        background: `${scenario.color}1A`,
                        color: scenario.color,
                      }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-gray-200">{step}</span>
                  </motion.li>
                ))}
              </ol>
            </div>
          </div>
        </div>

        {/* Right: description + products + CTA */}
        <div className="md:col-span-5">
          <p className="text-base sm:text-lg text-gray-300 leading-relaxed">
            {localized(scenario, "description")}
          </p>

          <button
            data-testid={SCENARIO.detailAdd}
            onClick={() => {
              toggleScenario(slug);
              toast.success(isSelected ? t.builder.clear : t.scenario.added);
            }}
            className={`mt-7 w-full inline-flex items-center justify-center gap-2 rounded-full font-semibold px-7 py-3.5 transition-all ${
              isSelected
                ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/20"
                : "bg-amber-500 hover:bg-amber-400 text-black hover:shadow-[0_0_30px_rgba(245,158,11,0.45)]"
            }`}
          >
            {isSelected ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
            {isSelected ? t.scenario.added : t.scenario.addToBuilder}
          </button>

          <div className="mt-10">
            <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-gray-400 mb-4">
              {t.scenario.products}
            </div>
            <div className="space-y-3">
              {requiredProducts.map((p) => (
                <div
                  key={p.slug}
                  className="flex items-start gap-3 rounded-2xl border border-white/[0.06] bg-[#0B0F19] p-4 hover:border-white/15 transition-colors"
                >
                  <div className="h-10 w-10 flex-shrink-0 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-gray-300">
                    <LucideIcon name={p.icon} className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-heading font-medium text-white text-base leading-tight">
                      {localized(p, "title")}
                    </div>
                    <div className="text-sm text-gray-400 mt-0.5">
                      {localized(p, "description")}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FlowBlock({ label, step, color, localized, solid }) {
  return (
    <div
      className="rounded-2xl border p-5 sm:p-6"
      style={{
        borderColor: solid ? color : "rgba(255,255,255,0.08)",
        background: solid ? `${color}14` : "rgba(255,255,255,0.02)",
      }}
    >
      <div
        className="text-[10px] tracking-[0.28em] uppercase font-bold mb-3"
        style={{ color: solid ? color : "#9CA3AF" }}
      >
        {label}
      </div>
      <div className="flex items-center gap-3">
        <div
          className="h-10 w-10 rounded-lg flex items-center justify-center"
          style={{
            background: `${color}1A`,
            border: `1px solid ${color}55`,
            color: color,
          }}
        >
          <LucideIcon name={step.icon} className="h-5 w-5" strokeWidth={2} />
        </div>
        <div className="font-heading text-lg leading-tight">{localized(step, "label")}</div>
      </div>
    </div>
  );
}
